import { useEffect, useState } from 'react';

interface ApiConfig {
  service_name: string;
  [key: string]: unknown;
}

interface WhatsAppRule {
  entity_type: string;
  active: boolean;
  [key: string]: unknown;
}

interface UseDealWhatsAppConfigParams {
  configurations: ApiConfig[];
  rules: WhatsAppRule[];
  fetchConfigurations: () => Promise<unknown>;
  fetchRules: () => Promise<unknown>;
}

interface UseDealWhatsAppConfigResult {
  whatsAppConfigured: boolean;
  dealNotificationsEnabled: boolean;
}

/**
 * Determina se mostrare il toggle "Invia notifiche WhatsApp" sull'offerta.
 *
 * Richiede:
 *  - configurazione WAHA presente in `configurations` (service_name === 'waha')
 *  - almeno una `WhatsAppAutomationRule` attiva su entity_type='deal'
 *
 * Fetcha configurations + rules al mount.
 *
 * Note: il side-effect "reset send_notifications quando non disponibile"
 * va gestito dal caller con un useEffect che dipende solo dai flag,
 * altrimenti una callback inline causerebbe un loop infinito.
 */
export function useDealWhatsAppConfig({
  configurations,
  rules,
  fetchConfigurations,
  fetchRules,
}: UseDealWhatsAppConfigParams): UseDealWhatsAppConfigResult {
  const [whatsAppConfigured, setWhatsAppConfigured] = useState(false);
  const [dealNotificationsEnabled, setDealNotificationsEnabled] = useState(false);

  // Fetch one-shot al mount
  useEffect(() => {
    (async () => {
      try {
        await fetchConfigurations();
        await fetchRules();
      } catch (error) {
        console.error('Error checking WhatsApp configuration:', error);
      }
    })();
  }, [fetchConfigurations, fetchRules]);

  // Derive flags (puro: nessun side-effect verso il caller).
  useEffect(() => {
    setWhatsAppConfigured(configurations.some(c => c.service_name === 'waha'));
    setDealNotificationsEnabled(rules.some(r => r.entity_type === 'deal' && r.active));
  }, [configurations, rules]);

  return { whatsAppConfigured, dealNotificationsEnabled };
}
